import { useCallback, useEffect, useMemo, useState } from 'react'
import { Coins, GraduationCap } from 'lucide-react'
import { useAuth } from '../../../../contexts/AuthContext'
import { Alert, AlertDescription } from '../../../../components/ui/alert'
import { Button } from '../../../../components/ui/button'
import { Skeleton } from '../../../../components/ui/skeleton'
import { Input } from '../../../../components/ui/input'
import { TableLoadingState } from '../../../../components/ui/table-loading-state'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../components/ui/table'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../../../components/ui/pagination'
import { fetchCreditPool, fetchCreditUsage } from '../../../../api/backend'
import type { CreditPoolStatus, CreditUsageResponse } from '../../../../api/backend'
import { getPaginatedItems, shouldShowPagination, USAGE_PAGE_SIZE } from './pagination'
import { ADMIN_PAGE_CONTENT_CLASSNAME } from '../layoutStyles'

const PERIOD_OPTIONS: Array<{ value: CreditUsageResponse['period']; label: string }> = [
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: 'all', label: 'All' },
]

const MONO_STYLE = {
  fontFamily: '"DM Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
} as const

function UsageMetricCardSkeleton() {
  return (
    <div
      className="rounded-base border-2 border-nb-border bg-white p-6 nb-shadow"
      style={{ backgroundColor: 'light-dark(lab(100% 0 0), lab(12% 0 0))' }}
    >
      <div className="flex items-center justify-between">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="size-5" />
      </div>
      <Skeleton className="mt-6 h-10 w-28" />
      <Skeleton className="mt-3 h-4 w-40" />
      <div className="mt-4 h-3 overflow-hidden rounded-full border-2 border-nb-border bg-background">
        <Skeleton className="h-full w-1/2 border-0 bg-main/30" />
      </div>
    </div>
  )
}

function UsageTableSkeleton({
  title,
  description,
  hasSearch = false,
  columns,
}: {
  title: string
  description: string
  hasSearch?: boolean
  columns: string[]
}) {
  return (
    <section
      className="rounded-base border-2 border-nb-border bg-white p-5 nb-shadow"
      style={{ backgroundColor: 'light-dark(lab(100% 0 0), lab(12% 0 0))' }}
    >
      <div className="mb-4 flex items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-lg">{title}</h2>
          <p className="text-sm text-foreground/60">{description}</p>
        </div>
        {hasSearch ? <Skeleton className="h-10 w-full max-w-xs" /> : null}
      </div>
      <div className="overflow-hidden rounded-base border-2 border-nb-border">
        <Table>
          <TableHeader>
            <TableRow className="bg-main text-main-foreground hover:bg-main">
              {columns.map((column) => (
                <TableHead key={column}>{column}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 5 }).map((_, index) => (
              <TableRow key={index}>
                {columns.map((column, cellIndex) => (
                  <TableCell key={`${column}-${cellIndex}`}>
                    <Skeleton className={cellIndex === 0 ? 'h-4 w-24' : 'h-4 w-16'} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Skeleton className="h-10 w-10" />
        <Skeleton className="h-10 w-10" />
        <Skeleton className="h-10 w-10" />
      </div>
    </section>
  )
}

function buildPageNumbers(totalPages: number, currentPage: number): Array<number | 'ellipsis'> {
  const pageNumbers: Array<number | 'ellipsis'> = []

  if (totalPages <= 7) {
    for (let index = 0; index < totalPages; index += 1) {
      pageNumbers.push(index)
    }
    return pageNumbers
  }

  pageNumbers.push(0)
  if (currentPage > 2) pageNumbers.push('ellipsis')

  const start = Math.max(1, currentPage - 1)
  const end = Math.min(totalPages - 2, currentPage + 1)
  for (let index = start; index <= end; index += 1) {
    pageNumbers.push(index)
  }

  if (currentPage < totalPages - 3) pageNumbers.push('ellipsis')
  pageNumbers.push(totalPages - 1)

  return pageNumbers
}

export default function UsagePage() {
  const { getAccessToken } = useAuth()
  const [period, setPeriod] = useState<CreditUsageResponse['period']>('30d')
  const [creditPool, setCreditPool] = useState<CreditPoolStatus | null>(null)
  const [usage, setUsage] = useState<CreditUsageResponse | null>(null)
  const [search, setSearch] = useState('')
  const [classPage, setClassPage] = useState(0)
  const [studentPage, setStudentPage] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback((nextPeriod: CreditUsageResponse['period']) => {
    const token = getAccessToken()
    if (!token) return
    setIsLoading(true)
    setError(null)
    Promise.all([fetchCreditPool(token), fetchCreditUsage(token, nextPeriod)])
      .then(([pool, usageResponse]) => {
        setCreditPool(pool)
        setUsage(usageResponse)
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load usage analytics')
      })
      .finally(() => setIsLoading(false))
  }, [getAccessToken])

  useEffect(() => {
    load(period)
  }, [load, period])

  const filteredStudents = useMemo(() => {
    if (!usage) return []
    const keyword = search.trim().toLowerCase()
    if (!keyword) return usage.byStudent
    return usage.byStudent.filter((student) => student.studentName.toLowerCase().includes(keyword))
  }, [search, usage])

  const classRows = useMemo(() => (
    (usage?.byClass ?? []).filter((item) => item.classTitle?.trim() || typeof item.creditsUsed === 'number')
  ), [usage])

  useEffect(() => {
    setStudentPage(0)
  }, [search, usage])

  useEffect(() => {
    setClassPage(0)
  }, [usage])

  const paginatedClassRows = useMemo(
    () => getPaginatedItems(classRows, classPage, USAGE_PAGE_SIZE),
    [classRows, classPage],
  )

  const paginatedStudents = useMemo(
    () => getPaginatedItems(filteredStudents, studentPage, USAGE_PAGE_SIZE),
    [filteredStudents, studentPage],
  )

  const classPageCount = Math.ceil(classRows.length / USAGE_PAGE_SIZE)
  const studentPageCount = Math.ceil(filteredStudents.length / USAGE_PAGE_SIZE)
  const classPageNumbers = useMemo(() => buildPageNumbers(classPageCount, classPage), [classPageCount, classPage])
  const studentPageNumbers = useMemo(() => buildPageNumbers(studentPageCount, studentPage), [studentPageCount, studentPage])

  return (
    <div className={ADMIN_PAGE_CONTENT_CLASSNAME}>
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription className="flex items-center gap-3">
            <span>{error}</span>
            <button className="ml-auto underline underline-offset-2 hover:no-underline" onClick={() => load(period)}>
              Retry
            </button>
          </AlertDescription>
        </Alert>
      )}

      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          {PERIOD_OPTIONS.map((option) => (
            <Button
              key={option.value}
              variant={period === option.value ? 'default' : 'neutral'}
              size="sm"
              onClick={() => setPeriod(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </div>
        <p className="text-sm text-foreground/60">
          Usage overview based on the currently selected reporting window.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {isLoading ? (
          Array.from({ length: 2 }).map((_, index) => <UsageMetricCardSkeleton key={index} />)
        ) : (
          <>
            <div
              className="rounded-base border-2 border-nb-border bg-white p-6 nb-shadow"
              style={{ backgroundColor: 'light-dark(lab(100% 0 0), lab(12% 0 0))' }}
            >
              <div className="flex items-center justify-between">
                <p className="text-sm text-foreground/60">Credit Pool</p>
                <Coins className="size-5 text-main" />
              </div>
              <p className="mt-6 text-4xl font-bold" style={MONO_STYLE}>
                {creditPool?.remainingCredits?.toLocaleString() ?? '—'}
              </p>
              <p className="mt-2 text-sm text-foreground/60">
                {creditPool
                  ? `${creditPool.usedCredits.toLocaleString()} used of ${creditPool.totalCredits.toLocaleString()}`
                  : 'No credit pool data'}
              </p>
              <div className="mt-4 h-3 overflow-hidden rounded-full border-2 border-nb-border bg-background">
                <div
                  className="h-full bg-main transition-all"
                  style={{ width: `${Math.min(100, creditPool?.usagePercent ?? 0)}%` }}
                />
              </div>
            </div>

            <div
              className="rounded-base border-2 border-nb-border bg-white p-6 nb-shadow"
              style={{ backgroundColor: 'light-dark(lab(100% 0 0), lab(12% 0 0))' }}
            >
              <div className="flex items-center justify-between">
                <p className="text-sm text-foreground/60">Active Students</p>
                <GraduationCap className="size-5 text-main" />
              </div>
              <p className="mt-6 text-4xl font-bold" style={MONO_STYLE}>
                {usage?.byStudent.length ?? 0}
              </p>
              <p className="mt-2 text-sm text-foreground/60">
                Students with tracked activity during the selected period.
              </p>
            </div>
          </>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
        {isLoading ? (
          <>
            <UsageTableSkeleton
              title="By Class"
              description="Credit usage across classes."
              columns={['Class', 'Credits']}
            />
            <UsageTableSkeleton
              title="By Student"
              description="Student-level activity and credit usage."
              columns={['#', 'Student', 'Credits']}
              hasSearch
            />
          </>
        ) : null}

        {!isLoading ? (
        <section
          className="rounded-base border-2 border-nb-border bg-white p-5 nb-shadow"
          style={{ backgroundColor: 'light-dark(lab(100% 0 0), lab(12% 0 0))' }}
        >
          <div className="mb-4 flex items-center justify-between gap-4">
            <div>
              <h2 className="font-heading text-lg">By Class</h2>
              <p className="text-sm text-foreground/60">Credit usage across classes.</p>
            </div>
          </div>
          <div className="overflow-hidden rounded-base border-2 border-nb-border">
            <Table>
              <TableHeader>
                <TableRow className="bg-main text-main-foreground hover:bg-main">
                  <TableHead>Class</TableHead>
                  <TableHead>Credits</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableLoadingState colSpan={2} label="Loading class usage..." />
                ) : classRows.length > 0 ? (
                  paginatedClassRows.map((item) => (
                    <TableRow key={item.classId} className="hover:bg-main/10">
                      <TableCell>{item.classTitle}</TableCell>
                      <TableCell className="font-base" style={MONO_STYLE}>{item.creditsUsed}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} className="py-10 text-center text-sm text-foreground/40">
                      No class usage data in this reporting window.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {!isLoading && shouldShowPagination(classRows.length, USAGE_PAGE_SIZE) ? (
            <div className="mt-4 flex justify-end">
              <Pagination className="w-auto mx-0">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      onClick={(event) => {
                        event.preventDefault()
                        if (classPage > 0) setClassPage((prev) => prev - 1)
                      }}
                      aria-disabled={classPage === 0}
                      className={classPage === 0 ? 'pointer-events-none opacity-40' : ''}
                    />
                  </PaginationItem>
                  {classPageNumbers.map((pageNumber, index) =>
                    pageNumber === 'ellipsis' ? (
                      <PaginationItem key={`class-ellipsis-${index}`}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    ) : (
                      <PaginationItem key={pageNumber}>
                        <PaginationLink
                          href="#"
                          isActive={pageNumber === classPage}
                          onClick={(event) => {
                            event.preventDefault()
                            setClassPage(pageNumber)
                          }}
                        >
                          {pageNumber + 1}
                        </PaginationLink>
                      </PaginationItem>
                    ),
                  )}
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      onClick={(event) => {
                        event.preventDefault()
                        if (classPage < classPageCount - 1) setClassPage((prev) => prev + 1)
                      }}
                      aria-disabled={classPage >= classPageCount - 1}
                      className={classPage >= classPageCount - 1 ? 'pointer-events-none opacity-40' : ''}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          ) : null}
        </section>
        ) : null}

        {!isLoading ? (
        <section
          className="rounded-base border-2 border-nb-border bg-white p-5 nb-shadow"
          style={{ backgroundColor: 'light-dark(lab(100% 0 0), lab(12% 0 0))' }}
        >
          <div className="mb-4 flex items-center justify-between gap-4">
            <div>
              <h2 className="font-heading text-lg">By Student</h2>
              <p className="text-sm text-foreground/60">Student-level activity and credit usage.</p>
            </div>
            <div className="w-full max-w-xs">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search students..."
              />
            </div>
          </div>
          <div className="overflow-hidden rounded-base border-2 border-nb-border">
            <Table>
              <TableHeader>
                <TableRow className="bg-main text-main-foreground hover:bg-main">
                  <TableHead className="w-14">#</TableHead>
                  <TableHead>Student</TableHead>
                  <TableHead>Credits</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableLoadingState colSpan={3} label="Loading student usage..." />
                ) : filteredStudents.length > 0 ? (
                  paginatedStudents.map((student, index) => (
                    <TableRow key={student.userId} className="hover:bg-main/10">
                      <TableCell className="text-sm text-foreground/60" style={MONO_STYLE}>
                        {studentPage * USAGE_PAGE_SIZE + index + 1}
                      </TableCell>
                      <TableCell>{student.studentName}</TableCell>
                      <TableCell className="font-base" style={MONO_STYLE}>{student.creditsUsed}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} className="py-10 text-center text-sm text-foreground/40">
                      {search.trim()
                        ? 'No students match your search in this reporting window.'
                        : 'No student usage data in this reporting window.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {!isLoading && shouldShowPagination(filteredStudents.length, USAGE_PAGE_SIZE) ? (
            <div className="mt-4 flex justify-end">
              <Pagination className="w-auto mx-0">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      onClick={(event) => {
                        event.preventDefault()
                        if (studentPage > 0) setStudentPage((prev) => prev - 1)
                      }}
                      aria-disabled={studentPage === 0}
                      className={studentPage === 0 ? 'pointer-events-none opacity-40' : ''}
                    />
                  </PaginationItem>
                  {studentPageNumbers.map((pageNumber, index) =>
                    pageNumber === 'ellipsis' ? (
                      <PaginationItem key={`student-ellipsis-${index}`}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    ) : (
                      <PaginationItem key={pageNumber}>
                        <PaginationLink
                          href="#"
                          isActive={pageNumber === studentPage}
                          onClick={(event) => {
                            event.preventDefault()
                            setStudentPage(pageNumber)
                          }}
                        >
                          {pageNumber + 1}
                        </PaginationLink>
                      </PaginationItem>
                    ),
                  )}
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      onClick={(event) => {
                        event.preventDefault()
                        if (studentPage < studentPageCount - 1) setStudentPage((prev) => prev + 1)
                      }}
                      aria-disabled={studentPage >= studentPageCount - 1}
                      className={studentPage >= studentPageCount - 1 ? 'pointer-events-none opacity-40' : ''}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          ) : null}
        </section>
        ) : null}
      </div>
    </div>
  )
}
